/**
 * Audit trail models for משימות שינוע 99.
 *
 * The ledger exists for the decisions the system did not make by itself: a
 * manager assigning a soldier or a vehicle over a warning ("שבץ בכל זאת") is
 * recorded here with the reason they gave, their user id and the moment they
 * gave it. Rows are append-only — nothing in the app rewrites one — so a
 * re-saved team can never quietly erase the decision that built it.
 *
 * The store lives in `src/db/audit-store.ts`; the override shape it carries is
 * {@link AssignmentOverride} from `./assignment`.
 */
import type { ISODateString } from './index';
import type { AssignmentOverride } from './assignment';
import type { UserRole } from './user';

/** What was done. New actions are appended, never renumbered. */
export const AUDIT_ACTIONS = [
  'ASSIGNMENT_OVERRIDE',
  'TEAM_SAVED',
  'TEAM_DELETED',
  'TASK_RECEIPT_CONFIRMED',
  'SOLDIER_TASK_UPDATE',
  'SOLDIER_FINISHED',
  'TASK_CLOSED',
  'TASK_DELETED',
  'TASK_RESTORED',
  'TASK_PURGED',
  'QUICK_TASK_LOGGED',
  'PROFILE_UPDATED',
  'VEHICLE_LOANED',
  'VEHICLE_RETURNED',
  'VEHICLE_STATUS_CHANGED',
  'VEHICLE_UPDATED',
  'SOLDIER_STATUS_CHANGED',
  'SOLDIER_UPDATED',
  'USER_CREATED',
  'USER_ROLE_CHANGED',
  'USER_ACCESS_CHANGED',
  'USER_PIN_RESET',
  'USER_PERMISSION_CHANGED',
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  ASSIGNMENT_OVERRIDE: 'שיבוץ בכל זאת — עקיפת התראה',
  TEAM_SAVED: 'שמירת צוות ורכב',
  TEAM_DELETED: 'הסרת צוות ורכב',
  TASK_RECEIPT_CONFIRMED: 'אישור קבלת משימה',
  SOLDIER_TASK_UPDATE: 'עדכון מהשטח',
  SOLDIER_FINISHED: 'סימון סיום ביצוע על ידי חייל',
  TASK_CLOSED: 'סגירת משימה על ידי מנהל',
  TASK_DELETED: 'העברת משימה לסל המחזור',
  TASK_RESTORED: 'שחזור משימה מסל המחזור',
  TASK_PURGED: 'מחיקת משימה לצמיתות',
  QUICK_TASK_LOGGED: 'רישום משימה מהירה לנהג',
  PROFILE_UPDATED: 'עדכון פרטים אישיים',
  VEHICLE_LOANED: 'השאלת רכב לפלוגה',
  VEHICLE_RETURNED: 'החזרת רכב לשינוע',
  VEHICLE_STATUS_CHANGED: 'שינוי מצב תפעולי לרכב',
  VEHICLE_UPDATED: 'עדכון פרטי רכב',
  SOLDIER_STATUS_CHANGED: 'שינוי סטטוס לחייל',
  SOLDIER_UPDATED: 'עדכון פרטי חייל',
  USER_CREATED: 'פתיחת חשבון משתמש',
  USER_ROLE_CHANGED: 'שינוי רמת הרשאה',
  USER_ACCESS_CHANGED: 'הפעלה או השבתה של גישה',
  USER_PIN_RESET: 'איפוס קוד כניסה',
  USER_PERMISSION_CHANGED: 'שינוי הרשאה פרטנית',
};

/**
 * The rows a record's "היסטוריית שינויים" is read off — every change somebody
 * made to it by hand, newest first. A loan is a change of hands and belongs in
 * the same feed as a change of status, so the two transfer actions are here as
 * well as in {@link VEHICLE_TRANSFER_ACTIONS}.
 */
export const CHANGE_HISTORY_ACTIONS = [
  'VEHICLE_STATUS_CHANGED',
  'VEHICLE_UPDATED',
  'VEHICLE_LOANED',
  'VEHICLE_RETURNED',
  'SOLDIER_STATUS_CHANGED',
  'SOLDIER_UPDATED',
] as const;

export type ChangeHistoryAction = (typeof CHANGE_HISTORY_ACTIONS)[number];

/** The actions the user-directory screen files against an account. */
export const USER_ADMIN_ACTIONS = [
  'USER_CREATED',
  'USER_ROLE_CHANGED',
  'USER_ACCESS_CHANGED',
  'USER_PIN_RESET',
  'USER_PERMISSION_CHANGED',
] as const;

export type UserAdminAction = (typeof USER_ADMIN_ACTIONS)[number];

/** The two rows that make up a vehicle's loan history, newest first. */
export const VEHICLE_TRANSFER_ACTIONS = ['VEHICLE_LOANED', 'VEHICLE_RETURNED'] as const;

export type VehicleTransferAction = (typeof VEHICLE_TRANSFER_ACTIONS)[number];

/** Which record the action was taken on. */
export const AUDIT_ENTITY_TYPES = ['task', 'taskTeam', 'soldier', 'vehicle', 'user'] as const;

export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number];

/**
 * One line of the ledger. `metadata` carries the machine-readable payload —
 * for an override, the {@link AssignmentOverride} verbatim — while `summary`
 * is the Hebrew sentence a log view renders without unpacking anything.
 */
export interface AuditLogEntry {
  id: string;
  action: AuditAction;
  entityType: AuditEntityType;
  entityId: string;
  /** The `User.id` that took responsibility for the action. */
  actorId: string;
  /**
   * The actor's name as it stood when they acted. Copied onto the row rather
   * than joined at read time on purpose: a ledger that renamed its own history
   * every time somebody edited their profile would not be a ledger. The same
   * reasoning applies to {@link actorRole} and {@link actorRoleLabel}.
   */
  actorName?: string;
  /** The access role the actor held at the moment they acted. */
  actorRole?: UserRole;
  /**
   * What the unit calls that role — the actor's job off the roster ("סמ"פ")
   * where they have one, and their access level otherwise. This is the string
   * "שונה ע״י אשר אמר (סמ"פ)" is built from; see `describeActorRole()` in
   * `src/core/access-levels.ts`.
   */
  actorRoleLabel?: string;
  /** The task the action belongs to, when it is not the entity itself. */
  taskId?: string;
  summary: string;
  /** The override this row records, when the action is an override. */
  override?: AssignmentOverride;
  /** Field-by-field before/after, for a row that records an edit. */
  changes?: readonly FieldChange[];
  metadata?: Record<string, unknown>;
  createdAt: ISODateString;
}

/**
 * One field that moved, in the words the screen shows — `label` and both values
 * are already Hebrew, because the ledger is read long after the code that wrote
 * it has changed its mind about how to spell an enum.
 */
export interface FieldChange {
  /** Machine-readable field name, e.g. `status` or `availability`. */
  field: string;
  label: string;
  from: string;
  to: string;
}

/** What a caller hands the store — ids and stamps are the store's job. */
export type AuditLogDraft = Omit<AuditLogEntry, 'id' | 'createdAt'> & {
  id?: string;
  createdAt?: ISODateString;
};
